import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FaTrash, FaUser, FaCheck } from 'react-icons/fa';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import './ChatRoomList.scss';
import fnc from '../../mymod/commFunction';

const ChatRoomList = ({ 
  chatrooms, 
  currentChatroom, 
  onSelectChatroom, 
  onCreateChatroom,
  onDeleteChatroom,
  currentUserId 
}) => {

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [userNamesCache, setUserNamesCache] = useState({}); // 사용자 이름 캐시

  const userInfo = useSelector((state) => state.reduxStore.userInfo);
  const userInfo2 = useSelector((state) => state.reduxStore.userInfo2);
  
  // 이미 로드 중인 사용자 ID 추적 (중복 API 호출 방지)
  const loadingUsersRef = useRef(new Set());

  // 사용자 목록 조회
  useEffect(() => {
    if (showCreateForm) {
      loadUsers();
    }
  }, [showCreateForm]);

  // 참여자 이름 로드 (무한루프 방지 버전)
  useEffect(() => {
    if (!chatrooms || chatrooms.length === 0) return;

    // 모든 참여자 ID 수집
    const allParticipantIds = [];
    chatrooms.forEach(chatroom => {
      if (chatroom.participants) {
        chatroom.participants.forEach(participantId => {
          // 자신은 제외, 캐시에 없고, 로딩 중이 아닌 경우만 추가
          if (participantId !== currentUserId && 
              !userNamesCache[participantId] && 
              !loadingUsersRef.current.has(participantId)) {
            allParticipantIds.push(participantId);
          }
        });
      }
    });

    // 새로운 사용자가 없으면 종료
    if (allParticipantIds.length === 0) return;

    console.log('🔵 새로운 사용자 이름 로드:', allParticipantIds);

    // 사용자 이름 로드
    const loadUserNames = async () => {
      for (const firebaseUserId of allParticipantIds) {
        // 로딩 중으로 표시
        loadingUsersRef.current.add(firebaseUserId);
        
        const idParts = firebaseUserId.split('_');
        const actualId = idParts.length > 1 ? idParts[1] : firebaseUserId;
        
        try {
          await new Promise((resolve) => {
            fnc.sendAxios('/socialLoginRoutes/select/userInfoById', 
              { id: actualId }, 
              (res) => {
                if (res && Array.isArray(res) && res.length > 0) {
                  const userName = res[0]?.nickname || res[0]?.name || actualId;
                  console.log('✅ 사용자 이름 로드 완료:', actualId, '→', userName);
                  // 개별 업데이트 (무한루프 방지)
                  setUserNamesCache(prev => ({
                    ...prev,
                    [firebaseUserId]: userName
                  }));
                } else {
                  setUserNamesCache(prev => ({
                    ...prev,
                    [firebaseUserId]: actualId
                  }));
                }
                resolve();
              }
            );
          });
        } catch (error) {
          console.error('사용자 이름 로드 오류:', error);
          setUserNamesCache(prev => ({
            ...prev,
            [firebaseUserId]: actualId
          }));
        } finally {
          // 로딩 완료
          loadingUsersRef.current.delete(firebaseUserId);
        }
      }
    };

    loadUserNames();
  }, [chatrooms.length, currentUserId]); // userNamesCache 제거하여 무한루프 방지


  // const loadUsers = () => {
  //   fnc.sendAxios('/socialLoginRoutes/select/allUsers', {}, (res) => {
  //     if (res && Array.isArray(res)) {
  //       // 현재 사용자 제외
  //       const otherUsers = res.filter(user => user.id !== currentUserId);
  //       setUsers(otherUsers);
  //     }
  //   });
  // };


  /**
   * 학원 내 전체 사용자 목록 로드 (현재 사용자 제외)
   * 새 채팅방 생성 시 참여자 선택에 사용
   */
  const loadUsers = () => {
    fnc.sendAxios('/socialLoginRoutes/select/allUsers2', { acaCode: userInfo2?.acaCode }, (res) => {
      if (res && Array.isArray(res)) {
        const otherUsers = res.filter(user => user.id !== currentUserId);
        setUsers(otherUsers);
      }
    });
  };

  // 사용자 선택/해제
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // 검색 필터링
  const filteredUsers = users.filter(user => 
    !searchKeyword || 
    user.nickname?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    user.id?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  // 새 채팅방 생성 핸들러
  const handleCreateRoom = (e) => {
    e.preventDefault();
    if (newRoomName.trim()) {
      onCreateChatroom(newRoomName.trim(), selectedUsers);
      setNewRoomName('');
      setSelectedUsers([]);
      setSearchKeyword('');
      setShowCreateForm(false);
    }
  };

  /**
   * 마지막 메시지 시간 포맷팅
   * @param {Timestamp|Date} timestamp - Firebase Timestamp 또는 Date 객체
   * @returns {string} 포맷된 시간 문자열
   */
  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const messageDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes < 1 ? '방금' : `${diffInMinutes}분 전`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}시간 전`;
    } else {
      return messageDate.toLocaleDateString('ko-KR', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  /**
   * 읽지 않은 메시지 수 계산
   * @param {Object} chatroom - 채팅방 데이터
   * @returns {number} 읽지 않은 메시지 수
   */
  const getUnreadCount = (chatroom) => {
    const count = chatroom.unreadCount?.[currentUserId] || 0;
    return Math.max(0, count); // 음수 방지
  };

  /**
   * 읽지 않은 메시지 존재 여부 확인
   * @param {Object} chatroom - 채팅방 데이터
   * @returns {boolean} 읽지 않은 메시지가 있는지 여부
   */
  const hasUnreadMessages = (chatroom) => {
    const hasUnread = getUnreadCount(chatroom) > 0;
    return hasUnread;
  };

  //======================================================================
  return (
    <div className="chatroom-list">
      <div className="chatroom-list-header">
        <h3>채팅방</h3>
        <button 
          className="create-room-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
          title="새 채팅방 만들기"
        >
          +
        </button>
      </div>

      {/* 새 채팅방 생성 폼 */}
      {showCreateForm && (
        <div className="create-room-form">
          <form onSubmit={handleCreateRoom}>
            <input
              type="text"
              placeholder="채팅방 이름을 입력하세요"
              value={newRoomName}
              onChange={(e) => setNewRoomName(e.target.value)}
              autoFocus
              required
            />

            {/* 참여자 선택 */}
            {/* <div className="participants-section">
              <label>참여자 선택 ({selectedUsers.length}명 선택됨)</label>
              
              <input
                type="text"
                placeholder="사용자 검색..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="search-input"
              />

              <div className="users-list">
                {filteredUsers.length === 0 ? (
                  <p className="no-users">사용자가 없습니다.</p>
                ) : (
                  filteredUsers.map(user => (
                    <div
                      key={user.id}
                      className={`user-item ${selectedUsers.includes(user.id) ? 'selected' : ''}`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="user-avatar">
                        {user.profile_image ? (
                          <img src={user.profile_image} alt={user.nickname} />
                        ) : (
                          <span>{user.nickname?.charAt(0)?.toUpperCase() || '?'}</span>
                        )}
                      </div>
                      <div className="user-info">
                        <span className="user-nickname">{user.nickname || '사용자'}</span>
                        <span className="user-id">{user.id2 || user.id}</span>
                      </div>
                      {selectedUsers.includes(user.id) && (
                        <FaCheck className="check-icon" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div> */}

            <div className="form-buttons">
              <button 
                type="submit" 
                className="create-btn"
                disabled={!newRoomName.trim()}
              >
                생성
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewRoomName('');
                  setSelectedUsers([]);
                  setSearchKeyword('');
                }}
              >
                취소
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 채팅방 목록 */}
      <div className="chatroom-items">
        {chatrooms.length === 0 ? (
          <div className="no-chatrooms">
            <p>채팅방이 없습니다.</p>
            <button onClick={() => setShowCreateForm(true)}>
              새 채팅방 만들기
            </button>
          </div>
        ) : (
          chatrooms.map(chatroom => (
            <div
              key={chatroom.id}
              className={`chatroom-item ${
                currentChatroom?.id === chatroom.id ? 'active' : ''
              } ${
                hasUnreadMessages(chatroom) ? 'has-unread' : ''
              }`}
              onClick={() => onSelectChatroom(chatroom)}
            >
              <div className="chatroom-avatar">
                <div className="avatar-circle">
                  {chatroom.name.charAt(0).toUpperCase()}
                </div>
                {chatroom.isOnline && (
                  <div className="online-indicator"></div>
                )}
                {hasUnreadMessages(chatroom) && (
                  <div className="unread-indicator"></div>
                )}
              </div>
              
              <div className="chatroom-info">
                <div className="chatroom-header">
                  <h4 className={`chatroom-name ${
                    hasUnreadMessages(chatroom) ? 'has-unread-text' : ''
                  }`}>
                    {chatroom.name}
                  </h4>
                  <div className="participants-count">
                    <FaUser size={10} />
                    <span>{chatroom.participants?.length || 0}</span>
                    {/* 참여자 이름 표시 (캐시 사용, 무한루프 방지) */}
                    {chatroom.participants && chatroom.participants.length > 0 && (
                      <span className="participant-names">
                        {chatroom.participants
                          .filter(participantId => participantId !== currentUserId)
                          .map(participantId => {
                            // 캐시에서 이름 가져오기, 없으면 ID 표시
                            if (userNamesCache[participantId]) {
                              return userNamesCache[participantId];
                            }
                            // 로딩 중이면 "..."
                            if (loadingUsersRef.current.has(participantId)) {
                              return '...';
                            }
                            // 캐시에 없으면 ID 표시
                            const idParts = participantId.split('_');
                            return idParts.length > 1 ? idParts[1] : participantId;
                          })
                          .join(', ')
                        }
                      </span>
                    )}
                  </div>
                  <div className="time-and-unread">
                    <span className="last-message-time">
                      {formatLastMessageTime(chatroom.lastMessageTime)}
                    </span>
                    {hasUnreadMessages(chatroom) && (
                      <span className="unread-badge">
                        {getUnreadCount(chatroom)}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="chatroom-footer">
                  <p className={`last-message ${
                    hasUnreadMessages(chatroom) ? 'has-unread-text' : ''
                  }`}>
                    {chatroom.lastMessage || '메시지가 없습니다.'}
                  </p>
                </div>
              </div>
              
              {/* 채팅방 생성자만 삭제 버튼 표시 */}
              {chatroom.createdBy === currentUserId && (
                <button
                  className="delete-chatroom-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`"${chatroom.name}" 채팅방을 삭제하시겠습니까?`)) {
                      onDeleteChatroom(chatroom.id);
                    }
                  }}
                  title="채팅방 삭제"
                >
                  <FaTrash size={12} />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ChatRoomList;